/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import { ReactNode } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";

import {
  Box,
  Drawer,
  Hidden,
  List,
  ListSubheader,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { User } from "react-feather";
import Logo from "src/components/Logo";
import NavItem from "./NavItem";
import { useTranslation } from "react-i18next";
import { homePath, profilePath } from "src/utils/paths";
import useSettings from "src/hooks/useSettings";
import { Theme } from "src/theme";
import useAuth from "src/hooks/useAuth";
import { ContextSelector } from "./ContextSelector";
import { history } from "../../../App";

interface NavBarProps {
  onMobileClose: () => void;
  openMobile: boolean;
}

interface Item {
  href?: string;
  icon?: ReactNode;
  info?: ReactNode;
  items?: Item[];
  title: string;
  preview?: boolean;
}

interface Section {
  items: Item[];
  subheader: string;
}

// Add items to your navigation here!!
const sections: Section[] = [
  {
    subheader: "views.profile.title",
    items: [
      {
        title: "??",
        icon: User,
        href: profilePath,
      },
    ],
  },
];

function reduceChildRoutes({
  acc,
  pathname,
  item,
  preview,
  depth,
}: {
  acc: any[];
  pathname: string;
  item: Item;
  depth: number;
  preview?: boolean;
}) {
  const key = item.title + depth;
  if (item.preview && !preview) return acc;
  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

function renderNavItems({
  items,
  pathname,
  preview,
  depth = 0,
}: {
  items: Item[];
  pathname: string;
  preview?: boolean;
  depth?: number;
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) =>
          //@ts-ignore
          reduceChildRoutes({ acc, item, pathname, depth, preview }),
        []
      )}
    </List>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  mobileDrawer: {
    width: 256,
    background: theme.palette.background.dark,
  },
  desktopDrawer: {
    width: 304,
    height: "100%",
    border: "none",
    background: theme.palette.background.dark,
  },
  avatar: {
    cursor: "pointer",
    width: 64,
    height: 64,
  },
  logo: {
    width: theme.spacing(11),
  },
  header: {
    background: theme.palette.background.dark,
    borderRadius: "0px 0px 0px 32px",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  content: {
    borderRadius: "0px 32px 0px 0px",
    background: theme.palette.background.dark,
  },
  subheader: {
    color: theme.palette.primary.main,
  },
}));

const NavBar: React.FC<NavBarProps> = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { settings } = useSettings();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  sections[0].items[0].title = user?.name || "-";
  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <RouterLink to={homePath} style={{ textDecoration: "none" }}>
          <Box
            className={classes.header}
            onClick={() => history.push(homePath)}
          >
            <Logo className={classes.logo} />
            <Typography
              variant="button"
              style={{
                marginLeft: "1rem",
                textTransform: "unset",
                textDecoration: "none",
              }}
            >
              BRITA Water Map
            </Typography>
          </Box>
        </RouterLink>
        <Box p={3} className={classes.content}>
          <Box
            position="absolute"
            zIndex="-1"
            width="100%"
            height="200px"
            top="0%"
            left="50%"
            bgcolor="#F4F6F8"
          />
          <ContextSelector />
          {sections.map((section) => (
            <List
              key={section.subheader}
              subheader={
                <ListSubheader
                  className={classes.subheader}
                  disableGutters
                  disableSticky
                >
                  {t(section.subheader)}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items.map((i) => ({ ...i, title: t(i.title) })),
                pathname: location.pathname,
                preview: settings.preview,
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden mdUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden smDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

export default NavBar;
